import { Controller } from "@hotwired/stimulus"

// Phase 2.10 Tier 4 — Folder Activity slide-over.
//
// Companion to `documents/_folder_activity.html.erb`. The partial
// renders the rollup contents; this controller hosts the slide-over
// shell and lazy-fetches the partial fragment from
// `/document_locking/folder_activity?folder_type=&folder_id=` when
// triggered.
//
// Listens for the `folder-activity-drawer:open` window event with
// `{ folderType, folderId }` detail. The documents / drawings_specs /
// specifications / closeout views dispatch this event from a toolbar
// button after resolving the user's currently-selected folder. Mirrors
// the dispatch pattern used by `document-audit-drawer:open` so each
// surface stays event-driven, without each view having to know about
// the drawer's internals.
//
// Fetched HTML is injected into a body slot; backdrop click + Esc both
// close per CLAUDE.md "Modal backdrop dismiss." Z-50 sits above the
// existing document audit drawer (z-40 backdrop / z-50 panel) so the
// two never visually conflict.
export default class extends Controller {
  static targets = ["root", "panel", "backdrop", "body", "title"]
  static values = {
    fetchUrl: String, // base URL — query string built per-open
  }

  connect() {
    this._onOpen = this._onOpen.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    window.addEventListener("folder-activity-drawer:open", this._onOpen)
    window.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    window.removeEventListener("folder-activity-drawer:open", this._onOpen)
    window.removeEventListener("keydown", this._onKeydown)
  }

  async _onOpen(event) {
    const detail = event?.detail || {}
    const folderType = detail.folderType
    const folderId = detail.folderId
    const title = detail.title || "Folder activity"

    if (!folderType || !folderId) {
      this._toast("Select a folder first.", "alert")
      return
    }

    this.titleTarget.textContent = title
    this.bodyTarget.innerHTML = `
      <div class="px-4 py-6 text-[13px] text-gray-500 text-center">Loading…</div>
    `
    this._show()

    const url = `${this.fetchUrlValue}?folder_type=${encodeURIComponent(folderType)}&folder_id=${encodeURIComponent(folderId)}`
    try {
      const resp = await fetch(url, {
        method: "GET",
        headers: { "Accept": "text/html" },
      })
      if (!resp.ok) {
        this.bodyTarget.innerHTML = `
          <div class="px-4 py-6 text-[13px] text-rose-600 text-center">Could not load folder activity.</div>
        `
        return
      }
      const html = await resp.text()
      this.bodyTarget.innerHTML = html
    } catch (e) {
      this.bodyTarget.innerHTML = `
        <div class="px-4 py-6 text-[13px] text-rose-600 text-center">Network error: ${e.message}</div>
      `
    }
  }

  close(event) {
    if (event) event.preventDefault()
    this._hide()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _show() {
    this.rootTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  _hide() {
    this.rootTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  _onKeydown(event) {
    if (event.key === "Escape" && !this.rootTarget.classList.contains("hidden")) {
      this._hide()
    }
  }

  _toast(msg, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.textContent = msg
    el.dataset.controller = "flash"
    el.dataset.flashDismissAfterValue = "5000"
    document.body.prepend(el)
  }
}
